import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ReportBuyOrSell } from "@/core";
import { useAvailableMonths } from "@/hooks/use-available-months";
import { useAvailableYears } from "@/hooks/use-available-years";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Filter, X } from "lucide-react";
import { useCallback, useEffect } from "react";
import { DateRange } from "react-day-picker";
 

export interface MonthlyReportFilters {
  searchTerm: string;
  dateRange: DateRange | undefined;
  year: string;
  month: string;
  buyOrSell: string;
  productId: string | null;
}

interface MonthlyReportFiltersProps {
  filters: MonthlyReportFilters;
  onFiltersChange: (filters: MonthlyReportFilters) => void;
  products: Array<{ id: string; name: string }>;
  isLoading?: boolean;
}

export function MonthlyReportFilters({
  filters,
  onFiltersChange,
  products = [],
  isLoading = false,
}: MonthlyReportFiltersProps) {
  const updateFilter = useCallback(
    <K extends keyof MonthlyReportFilters>(
      key: K,
      value: MonthlyReportFilters[K],
    ) => {
      onFiltersChange({ ...filters, [key]: value });
    },
    [filters, onFiltersChange],
  );

  const clearFilters = () => {
    onFiltersChange({
      searchTerm: "",
      dateRange: undefined,
      year: "all",
      month: "all",
      buyOrSell: "both",
      productId: null,
    });
  };

  const hasActiveFilters =
    filters.searchTerm ||
    filters.dateRange ||
    filters.year !== "all" ||
    filters.month !== "all" ||
    filters.buyOrSell !== "both" ||
    !!filters.productId;

  const activeFilterCount = [
    filters.searchTerm,
    filters.dateRange,
    filters.year !== "all" ? filters.year : null,
    filters.month !== "all" ? filters.month : null,
    filters.buyOrSell !== "all" ? filters.buyOrSell : null,
    filters.productId ? `Product: 1` : null,
  ].filter(Boolean).length;

  const {
    availableYears,
    isLoading: yearsLoading,
    hasYears,
  } = useAvailableYears();
  const {
    availableMonths,
    isLoading: monthsLoading,
    hasMonths,
  } = useAvailableMonths(filters.year);

  // Reset month filter when year changes and the selected month is not available
  useEffect(() => {
    if (filters.year !== "all" && filters.month !== "all" && hasMonths) {
      const isMonthAvailable = availableMonths.includes(
        parseInt(filters.month, 10),
      );
      if (!isMonthAvailable) {
        updateFilter("month", "all");
      }
    }
  }, [filters.year, filters.month, availableMonths, hasMonths, updateFilter]);

  const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  return (
    <Card className="flex-2/3 gap-1">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Monthly Filters
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground ml-2">
                {activeFilterCount}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={cn("space-y-4", "lg:block")}>
        <div className="flex gap-4 flex-wrap">
          {/* Year */}
          <div className="space-y-2">
            <Label htmlFor="year-select">Year</Label>
            <Select
              value={filters.year}
              onValueChange={(value) => updateFilter("year", value)}
              disabled={isLoading || yearsLoading}
            >
              <SelectTrigger
                id="year-select"
                className={cn("min-w-[140px]", yearsLoading && "animate-pulse")}
                aria-label="Select year filter"
              >
                <SelectValue
                  placeholder={yearsLoading ? "Loading..." : "Select year"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {yearsLoading ? (
                  <SelectItem value="loading" disabled>
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                      Loading years...
                    </div>
                  </SelectItem>
                ) : !hasYears ? (
                  <SelectItem value="no-data" disabled>
                    No years available
                  </SelectItem>
                ) : (
                  availableYears.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Month */}
          <div className="space-y-2">
            <Label htmlFor="month-select">Month</Label>
            <Select
              value={filters.month}
              onValueChange={(value) => updateFilter("month", value)}
              disabled={
                isLoading ||
                monthsLoading ||
                (filters.year !== "all" && !hasMonths)
              }
            >
              <SelectTrigger
                id="month-select"
                className={cn(
                  "min-w-[140px]",
                  monthsLoading && "animate-pulse",
                )}
                aria-label="Select month filter"
              >
                <SelectValue
                  placeholder={monthsLoading ? "Loading..." : "Select month"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {monthsLoading ? (
                  <SelectItem value="loading" disabled>
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                      Loading months...
                    </div>
                  </SelectItem>
                ) : filters.year !== "all" && !hasMonths ? (
                  <SelectItem value="no-data" disabled>
                    No months available for {filters.year}
                  </SelectItem>
                ) : filters.year === "all" ? (
                  months.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))
                ) : (
                  availableMonths.map((monthNum) => {
                    const monthData = months.find(
                      (m) => m.value === monthNum.toString(),
                    );
                    return monthData ? (
                      <SelectItem key={monthData.value} value={monthData.value}>
                        {monthData.label}
                      </SelectItem>
                    ) : null;
                  })
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Transaction Type */}
          <div className="space-y-2">
            <Label htmlFor="buyorsell-select">Transaction Type</Label>
            <Select
              value={filters.buyOrSell}
              onValueChange={(value) => updateFilter("buyOrSell", value)}
              disabled={isLoading}
            >
              <SelectTrigger
                id="buyorsell-select"
                className="min-w-[140px]"
                aria-label="Select transaction type filter"
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ReportBuyOrSell.BOTH}>All Types</SelectItem>
                <SelectItem value={ReportBuyOrSell.BUY}>Purchases</SelectItem>
                <SelectItem value={ReportBuyOrSell.SELL}>Sales</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Product Selector (single-select to match function payload) */}
          <div className="space-y-2">
            <Label>Products</Label>
            <Select
              value={filters.productId ?? "all"}
              onValueChange={(value) =>
                updateFilter("productId", value === "all" ? null : value)
              }
              disabled={isLoading}
            >
              <SelectTrigger className="min-w-[220px]">
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {products.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <>
            <Separator />
            <div className="space-y-2">
              <Label className="text-sm font-medium">Active Filters</Label>
              <div className="flex flex-wrap gap-2">
                {filters.searchTerm && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Search: {filters.searchTerm}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("searchTerm", "")}
                    />
                  </span>
                )}
                {filters.dateRange && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Date:{" "}
                    {filters.dateRange.from &&
                      format(filters.dateRange.from, "MMM dd")}{" "}
                    -{" "}
                    {filters.dateRange.to &&
                      format(filters.dateRange.to, "MMM dd")}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("dateRange", undefined)}
                    />
                  </span>
                )}
                {filters.year !== "all" && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Year: {filters.year}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("year", "all")}
                    />
                  </span>
                )}
                {filters.month !== "all" && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Month:{" "}
                    {months.find((m) => m.value === filters.month)?.label ||
                      filters.month}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("month", "all")}
                    />
                  </span>
                )}
                {filters.buyOrSell !== "all" && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Type: {filters.buyOrSell}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("buyOrSell", "all")}
                    />
                  </span>
                )}
                {filters.productId && (
                  <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden border-transparent bg-secondary text-secondary-foreground">
                    Product: {products.find((p) => p.id === filters.productId)?.name || filters.productId}
                    <X
                      className="h-4 w-4 cursor-pointer hover:text-destructive transition-colors"
                      onClick={() => updateFilter("productId", null)}
                    />
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
